import {
    useEffect,
    useRef,
    useState,
    useCallback,
} from "react";
import { useNavigate } from "react-router-dom";

const ImageSlider = ({
    slides,
    autoPlay = true,
    interval = 15000,
}) => {
    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);
    const sliderRef = useRef(null);
    const touchStartX = useRef(0);
    const navigate = useNavigate();

    /* ================= SLIDE CONTROLS ================= */
    const nextSlide = useCallback(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
    }, [slides.length]);

    const prevSlide = useCallback(() => {
        setCurrent((prev) =>
            prev === 0 ? slides.length - 1 : prev - 1
        );
    }, [slides.length]);

    /* ================= AUTO PLAY ================= */
    useEffect(() => {
        if (!autoPlay || paused) return;

        const timer = setInterval(() => {
            nextSlide();
        }, interval);

        return () => clearInterval(timer);
    }, [autoPlay, paused, interval, nextSlide]);

    /* ================= DYNAMIC HEIGHT ================= */
    useEffect(() => {
        const activeSlide =
            sliderRef.current?.children[current];
        if (activeSlide) {
            sliderRef.current.style.height = `${activeSlide.offsetHeight}px`;
        }
    }, [current]);

    /* ================= NAVIGATION + SCROLL ================= */
    const handleClick = (path, targetId) => {
    navigate(path);
    if (targetId) {
        setTimeout(() => {
            const el = document.getElementById(targetId);
            if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100); // slight delay to ensure navigation happened
    }
};


    /* ================= SHARE SLIDE ================= */
  const handleShare = async (slide) => {
    try {
        const shareText =
            `${slide.title}\n\n` +
            `${slide.description}\n\n` +
            `🔥 Open this link to Prophet Kevin N official web and go click on "select a vision" button for more details about this vision and more:`;

        // Construct full URL using backend domain
        const backendUrl = "https://prophet-kevin-online-website.onrender.com";
        const shareUrl = `${backendUrl}${slide.linkTo}`; // slide.linkTo can be '/books' etc.

        const shareData = {
            text: shareText,
            url: shareUrl,
        };

        // Image support
        if (slide.image && navigator.canShare) {
            const response = await fetch(slide.image);
            const blob = await response.blob();

            const file = new File([blob], "slide-image.jpg", { type: blob.type });

            if (navigator.canShare({ files: [file] })) {
                shareData.files = [file];
            }
        }

        if (navigator.share) {
            await navigator.share(shareData);
        } else {
            await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
            alert("Copied to clipboard!");
        }
    } catch (err) {
        console.error("Share failed:", err);
    }
};

 
    /* ================= SWIPE SUPPORT ================= */
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX.current - touchEndX;

        if (diff > 50) nextSlide();
        if (diff < -50) prevSlide();
    };

    return (
        <div
            className="relative w-full overflow-hidden h-[350px] rounded shadow-lg"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {/* SLIDER */}
            <div
                ref={sliderRef}
                className="flex transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${current * 100}%)` }}
            >
                {slides.map((slide, index) => (
                    <div
                        key={index}
                        className="relative min-w-full"
                    >
                        <button
                            onClick={() =>
                                handleClick(slide.linkTo, slide.scrollToId)
                            }
                            className="w-full h-full block"
                        >
                            <img
                                src={slide.image}
                                alt={slide.title}
                                className="w-full h-[350px] object-cover"
                            />

                            {/* OVERLAY */}
                            <div className="absolute inset-0 h-[350px] bg-black/40 flex flex-col justify-end p-6">
                                <h2 className="text-2xl font-bold text-white">
                                    {slide.title}
                                </h2>
                                <p className="mt-2 text-sm font-semibold text-white">
                                    {slide.description}
                                </p>
                            </div>
                        </button>

                        {/* SHARE BUTTON */}
                        <button
                            onClick={() => handleShare(slide)}
                            className="absolute top-[1px] left-[1px] bg-white/80 text-blue-600 font-bold px-3 py-1 rounded shadow hover:bg-white transition"
                        >
                            Share
                        </button>
                    </div>
                ))}
            </div>

            {/* PREVIOUS */}
            <button
                onClick={prevSlide}
                aria-label="Previous slide"
                className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white/70 hover:bg-white text-blue-600 font-bold rounded-full w-10 h-10 shadow"
            >
                ‹
            </button>

            {/* NEXT */}
            <button
                onClick={nextSlide}
                aria-label="Next slide"
                className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white/70 hover:bg-white text-blue-600 font-bold rounded-full w-10 h-10 shadow"
            >
                ›
            </button>
        </div>
    );
};

export default ImageSlider;
